import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { UnifiedStats } from "@/types/stats";
import { getGameStats } from "@/utils/gameProgress";

/**
 * Single source of truth for all game statistics.
 * This hook consolidates stats from Supabase (for authenticated users)
 * and localStorage (for guests), ensuring consistency across all components.
 */
export const useUnifiedStats = (): UnifiedStats & { refreshStats: () => Promise<void> } => {
  const { user, gameStats, statsLoading, refreshStats: refreshAuthStats } = useAuth();
  const [localStats, setLocalStats] = useState<ReturnType<typeof getGameStats> | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  // Load local stats for guest users
  useEffect(() => {
    if (!user) {
      setLocalStats(getGameStats());
    }
  }, [user]);

  // Force refresh stats from all sources
  const refreshStats = useCallback(async () => {
    if (user) {
      await refreshAuthStats();
    } else {
      setLocalStats(getGameStats());
    }
    setLastFetched(new Date());
  }, [user, refreshAuthStats]);

  // Auto-refresh when auth state changes
  useEffect(() => {
    refreshStats();
  }, [user?.id]);

  // Choose data source based on authentication
  const sourceStats = user ? gameStats : localStats;
  const isLoading = user ? statsLoading : false;

  // Extract guess distribution safely
  const sourceStatsAny = sourceStats as any;
  const guessDistribution: Record<number, number> = 
    sourceStatsAny?.guess_distribution || 
    sourceStatsAny?.guessDistribution || 
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  // Calculate derived stats
  const totalPlayed = (sourceStatsAny?.total_played ?? sourceStatsAny?.totalPlayed ?? 0) as number;
  const totalWon = (sourceStatsAny?.total_won ?? sourceStatsAny?.totalWon ?? 0) as number;
  const currentStreak = (sourceStatsAny?.current_streak ?? sourceStatsAny?.currentStreak ?? 0) as number;
  const maxStreak = (sourceStatsAny?.max_streak ?? sourceStatsAny?.maxStreak ?? 0) as number;

  const winPercentage = totalPlayed > 0 
    ? Math.round((totalWon / totalPlayed) * 100)
    : 0;

  const totalGuesses = Object.values(guessDistribution).reduce((sum, count) => sum + ((count as number) || 0), 0);
  const averageAttempts = totalGuesses > 0 
    ? parseFloat((Object.entries(guessDistribution).reduce((sum, [guess, count]) => sum + (parseInt(guess) * ((count as number) || 0)), 0) / totalGuesses).toFixed(1))
    : 0;

  const perfectGames = (guessDistribution[1] || 0) as number;

  return {
    // Core stats
    totalPlayed,
    totalWon,
    currentStreak,
    maxStreak,
    guessDistribution,
    
    // Pre-calculated derived values
    winPercentage,
    averageAttempts,
    perfectGames,
    
    // Meta
    isLoading,
    lastFetched,
    
    // Actions
    refreshStats,
  };
};
